/**
 * BCSubtypePanel.jsx — iter922 (Feb 2026)
 *
 * BC Molecular Subtype selection & nutritional targeting panel.
 * Renders in BHI. Lets the user (self-)declare their subtype and see
 * subtype-specific:
 *   • Molecular biology summary (biomarkers, KEGG pathways, epigenetics)
 *   • Nutritional themes (BC-Friendly menu targeting)
 *   • Citations (PMID / DOI) so the oncologist can look them up
 *
 * Educational-only positioning enforced by the "requires_consult" flag —
 * any AR-modulating theme surfaces a doctor-consult banner.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Dna, Sparkles, Loader2, CheckCircle2, AlertTriangle,
  BookOpen, Salad, ChevronDown, ChevronUp, Info,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGNOSIS_COLOR = {
  excellent:  "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300",
  very_good:  "bg-teal-500/20 text-teal-700 dark:text-teal-300",
  good:       "bg-lime-500/20 text-lime-700 dark:text-lime-300",
  moderate:   "bg-amber-500/20 text-amber-700 dark:text-amber-300",
  variable:   "bg-orange-500/20 text-orange-700 dark:text-orange-300",
  guarded:    "bg-rose-500/20 text-rose-700 dark:text-rose-300",
};

const PROGNOSIS_LABEL = {
  excellent:  "Отлична прогноза",
  very_good:  "Много добра",
  good:       "Добра",
  moderate:   "Умерена",
  variable:   "Вариабилна",
  guarded:    "Резервирана",
};

export default function BCSubtypePanel() {
  const { token } = useAuth();
  const { isDark } = useTheme();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [registry, setRegistry] = useState([]);
  const [mine, setMine] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reg, m] = await Promise.all([
        axios.get(`${API}/bc-subtypes/registry`),
        axios.get(`${API}/bc-subtypes/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setRegistry(reg.data?.subtypes || []);
      setMine(m.data || null);
    } catch (e) {
      console.warn("bc-subtypes load failed:", e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const declareSubtype = async (sid) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/bc-subtypes/mine`,
        { subtype_id: sid, self_declared: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Подтипът е записан. Менюто ще се персонализира.");
      await load();
    } catch (e) {
      toast.error("Грешка при записване. Опитайте отново.");
    } finally {
      setSaving(false);
    }
  };

  const clearSubtype = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/bc-subtypes/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Подтипът е изчистен.");
      await load();
    } catch (e) {
      toast.error("Грешка при изчистване.");
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (sid) => setExpanded((p) => ({ ...p, [sid]: !p[sid] }));

  if (loading) {
    return (
      <div className={`p-4 rounded-2xl ${isDark ? "bg-white/[0.04]" : "bg-white"} border ${isDark ? "border-white/10" : "border-gray-200"}`}
           data-testid="bc-subtype-panel-loading">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
        <span className="text-sm">Зарежда се молекулярна библиотека…</span>
      </div>
    );
  }

  const mineSubtype = mine?.declared ? registry.find((s) => s.id === mine.subtype_id) : null;
  const targeting = mine?.menu_targeting;

  return (
    <div className={`p-4 rounded-2xl ${isDark ? "bg-white/[0.04]" : "bg-white"} border ${isDark ? "border-white/10" : "border-gray-200"}`}
         data-testid="bc-subtype-panel">
      <div className="flex items-center gap-2 mb-3">
        <Dna className="w-5 h-5 text-pink-500" />
        <h3 className="text-sm font-extrabold">🎀 BC Молекулярен подтип → персонализирано меню</h3>
      </div>

      <p className="text-[11px] opacity-70 mb-4 leading-relaxed">
        Ако имате хистологично / IHC-потвърден молекулярен подтип, декларирайте го тук за да получите
        subtype-specific хранителни теми в BC-Friendly менюто. Регистърът обхваща 5 главни + 8 редки подтипа
        с ключови KEGG пътища, протеомика и епигенетика (13 общо).
      </p>

      {/* ── iter1017 — Explicit "Не съм диагностициран" opt-out banner ─── */}
      {!mineSubtype && (
        <div
          className={`mb-3 p-2.5 rounded-lg border flex items-start gap-2 text-[11px] ${
            isDark ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" : "bg-emerald-50 border-emerald-200 text-emerald-800"
          }`}
          data-testid="bc-subtype-optional-hint"
        >
          <Info className="w-3 h-3 shrink-0 mt-0.5" />
          <div>
            <strong>По желание.</strong> Ако не сте диагностициран или не знаете подтипа си,
            <strong> оставете това поле празно</strong> — Twin ще работи без subtype-specific усилване.
          </div>
        </div>
      )}

      {/* ── Declared state ─────────────────────────────────────── */}
      {mineSubtype && (
        <div className={`mb-4 p-3 rounded-xl border-l-4 border-pink-500 ${
          isDark ? "bg-pink-500/10" : "bg-pink-50"
        }`} data-testid="bc-subtype-declared">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-extrabold">{mineSubtype.name_bg}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                  PROGNOSIS_COLOR[mineSubtype.prognosis] || "bg-gray-400/25"
                }`}>
                  {PROGNOSIS_LABEL[mineSubtype.prognosis] || mineSubtype.prognosis}
                </span>
              </div>
              <div className="text-[10px] opacity-60 mt-0.5">{mineSubtype.name_en}</div>
            </div>
            <button
              onClick={clearSubtype}
              disabled={saving}
              className="text-[10px] px-2 py-1 rounded-md border border-current/20 opacity-70 hover:opacity-100"
              data-testid="bc-subtype-clear-btn"
            >
              Изчисти
            </button>
          </div>

          {/* Menu targeting */}
          {targeting?.priorities?.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Salad className="w-3.5 h-3.5 text-green-500" />
                <span className="text-[10px] font-bold uppercase tracking-wide opacity-80">
                  Хранителни приоритети за вашия подтип
                </span>
              </div>
              <ul className="space-y-1.5">
                {targeting.priorities.map((p) => (
                  <li key={p.id} className="text-[11px] leading-relaxed" data-testid={`bc-priority-${p.id}`}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-bold">{p.label_bg}</span>
                      {p.requires_doctor_consult === "true" && (
                        <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-rose-500/25 text-rose-700 dark:text-rose-300 font-bold">
                          ⚠ Изисква онколог
                        </span>
                      )}
                    </div>
                    <div className="opacity-70 text-[10px]">{p.examples_bg}</div>
                    <div className="opacity-60 text-[9px] italic mt-0.5">→ {p.rationale_bg}</div>
                  </li>
                ))}
              </ul>
              {targeting.requires_consult && (
                <div className={`mt-3 p-2 rounded-md text-[10px] flex items-start gap-1.5 ${
                  isDark ? "bg-rose-500/10 text-rose-300" : "bg-rose-50 text-rose-700"
                }`}>
                  <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                  <span>{targeting.regulatory_note}</span>
                </div>
              )}
            </div>
          )}

          {/* Biology detail (collapsible) */}
          <button
            onClick={() => toggleExpand(mineSubtype.id)}
            className="mt-3 flex items-center gap-1 text-[10px] font-bold opacity-70 hover:opacity-100"
            data-testid="bc-subtype-details-toggle"
          >
            {expanded[mineSubtype.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Молекулярни детайли (биомаркери, KEGG, епигенетика, PMIDs)
          </button>
          {expanded[mineSubtype.id] && (
            <div className="mt-2 space-y-2 text-[10px]" data-testid="bc-subtype-details">
              <div>
                <span className="font-bold opacity-80">Биомаркери: </span>
                {Object.entries(mineSubtype.biomarkers || {}).map(([k, v]) => `${k}=${v}`).join(" · ")}
              </div>
              <div>
                <span className="font-bold opacity-80">KEGG пътища: </span>
                <span className="font-mono">{(mineSubtype.kegg_pathways || []).join(", ")}</span>
              </div>
              <div>
                <span className="font-bold opacity-80">Протеомика: </span>
                {(mineSubtype.proteomic_markers || []).join(", ")}
              </div>
              <div>
                <span className="font-bold opacity-80">Епигенетика: </span>
                <ul className="ml-3 list-disc opacity-80">
                  {(mineSubtype.epigenetic_features || []).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
              <div>
                <span className="font-bold opacity-80">Терапевтични теми: </span>
                <ul className="ml-3 list-disc opacity-80">
                  {(mineSubtype.therapeutic_themes || []).map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
              <div>
                <span className="font-bold opacity-80">Референции: </span>
                <ul className="ml-3 list-disc opacity-80">
                  {(mineSubtype.citations || []).map((c, i) => (
                    <li key={i}>
                      {c.pmid && <span className="font-mono">PMID: {c.pmid}</span>}
                      {c.doi && <span className="font-mono">DOI: {c.doi}</span>}
                      {c.note ? ` — ${c.note}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Selector when not declared ─────────────────────────── */}
      {!mineSubtype && (
        <div data-testid="bc-subtype-selector">
          <div className="text-[11px] font-bold mb-2 opacity-80">
            Изберете подтип (само ако имате потвърдена диагноза):
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {registry.map((s) => (
              <button
                key={s.id}
                onClick={() => declareSubtype(s.id)}
                disabled={saving}
                className={`text-left p-2.5 rounded-xl border ${
                  isDark ? "border-white/10 hover:border-pink-400/50 bg-white/[0.02]" : "border-gray-200 hover:border-pink-400 bg-white"
                } transition-colors`}
                data-testid={`bc-subtype-option-${s.id}`}
              >
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[11px] font-bold">{s.name_bg}</span>
                  <span className={`text-[8px] px-1 py-0.5 rounded-full font-bold ${
                    PROGNOSIS_COLOR[s.prognosis] || "bg-gray-400/25"
                  }`}>
                    {PROGNOSIS_LABEL[s.prognosis] || s.prognosis}
                  </span>
                </div>
                <div className="text-[9px] opacity-60 mt-0.5">
                  {s.category === "rare_tnbc" ? "рядък TNBC подтип" : "главен подтип"}
                </div>
              </button>
            ))}
          </div>
          <div className={`mt-3 p-2 rounded-md text-[10px] flex items-start gap-1.5 ${
            isDark ? "bg-amber-500/10 text-amber-300" : "bg-amber-50 text-amber-800"
          }`}>
            <Info className="w-3 h-3 shrink-0 mt-0.5" />
            <span>
              Декларирането е доброволно и <strong>не заменя</strong> хистологичната диагноза.
              Всички препоръки са образователни и се обсъждат с онколог.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
